import { isDefinedArray, isDefinedString, reduce } from "et3-type-guards";
import i18n, { Ti18nKeys } from "../constants/i18n";
import {
  ALL_COUNTRY_PICKER_ENTRIES,
  BELONG_TO_A_TRIBE,
  EDUCATION_LEVEL,
  FEMALE_WORK_STUDY,
  HAVING_KIDS,
  INCOME,
  KHATTABAT_MATCHING_RESULTS_MATCH_APP_LANGUAGE,
  MARITAL_STATUS_FEMALE,
  MARITAL_STATUS_MALE,
  RELIGION,
  RELOCATION,
  SKIN_COLOR,
  SMOKING_LEVEL,
  TYPE_OF_MARIAGE,
  VEIL,
  WORK_LEVEL
} from "../constants/picker-entries-constants";
import { TKhattabatMatchmakingSearchCriteria } from "../Types";

type TCriteriaOption = { label: string; value: string };

const NO_SELECTION_VALUES = ["", "NONE", "0", "-1"];

const isSelected = (value: string) => {
  return isDefinedString(value) && !NO_SELECTION_VALUES.includes(`${value}`);
};

const getOptionLabel = (options: TCriteriaOption[], value: string) => {
  const option = options.filter((entry) => {
    return entry.value === `${value}`;
  })[0];
  return reduce(() => option.label, `${value}`);
};

const getOptionLabels = (options: TCriteriaOption[], values: string[]) => {
  return values
    .map((value) => {
      return getOptionLabel(options, value);
    })
    .join(", ");
};

const getMaritalStatusOptions = (matchGender: string) => {
  return matchGender === "f" ? MARITAL_STATUS_FEMALE : MARITAL_STATUS_MALE;
};

export const getReadableSearchCriteria = (
  criteria: TKhattabatMatchmakingSearchCriteria
) => {
  const parts: string[] = [];

  const addPart = (labelKey: Ti18nKeys, value: string) => {
    if (!isDefinedString(value)) {
      return;
    }
    parts.push(`${i18n.get(labelKey)}: ${value}`);
  };

  const addNumberPart = (labelKey: Ti18nKeys, value: number) => {
    if (!isSelected(`${value}`)) {
      return;
    }
    addPart(labelKey, `${value}`);
  };

  const addSingleChoicePart = (
    labelKey: Ti18nKeys,
    options: TCriteriaOption[],
    value: string
  ) => {
    if (!isSelected(value)) {
      return;
    }
    addPart(labelKey, getOptionLabel(options, value));
  };

  const addMultipleChoicePart = (
    labelKey: Ti18nKeys,
    options: TCriteriaOption[],
    values: string[],
    isExcluded: boolean
  ) => {
    if (!isDefinedArray(values)) {
      return;
    }
    const excludePrefix = isExcluded ? `[${i18n.get("EXCLUDE")}] ` : "";
    addPart(labelKey, `${excludePrefix}${getOptionLabels(options, values)}`);
  };

  addNumberPart(
    "MIN_AGE",
    reduce(() => criteria.minAge, 0)
  );
  addNumberPart(
    "MAX_AGE",
    reduce(() => criteria.maxAge, 0)
  );
  addNumberPart(
    "MIN_HEIGHT",
    reduce(() => criteria.minHeight, 0)
  );
  addNumberPart(
    "MAX_HEIGHT",
    reduce(() => criteria.maxHeight, 0)
  );
  addNumberPart(
    "MIN_WEIGHT",
    reduce(() => criteria.minWeight, 0)
  );
  addNumberPart(
    "MAX_WEIGHT",
    reduce(() => criteria.maxWeight, 0)
  );
  addMultipleChoicePart(
    "NATIONALITY",
    ALL_COUNTRY_PICKER_ENTRIES,
    reduce(() => criteria.nationalityCode, []),
    reduce(() => criteria.isChosenNationalitiesExcluded, false)
  );
  addMultipleChoicePart(
    "COUNTRY_OF_RESIDENCE",
    ALL_COUNTRY_PICKER_ENTRIES,
    reduce(() => criteria.countryCode, []),
    reduce(() => criteria.isChosenResidenceCountriesExcluded, false)
  );
  addMultipleChoicePart(
    "CITY_OF_RESIDENCE",
    [],
    reduce(() => criteria.residenceCity, []),
    false
  );
  addMultipleChoicePart(
    "TYPE_OF_MARIAGE",
    TYPE_OF_MARIAGE,
    reduce(() => criteria.typeOfMarriage, []),
    false
  );
  addMultipleChoicePart(
    "MARITAL_STATUS",
    getMaritalStatusOptions(reduce(() => criteria.matchGender, "")),
    reduce(() => criteria.maritalStatus, []),
    false
  );
  addSingleChoicePart(
    "HAVING_KIDS",
    HAVING_KIDS,
    reduce(() => criteria.havingKids, "")
  );
  addMultipleChoicePart(
    "WORK_LEVEL",
    WORK_LEVEL,
    reduce(() => criteria.work, []),
    false
  );
  addMultipleChoicePart(
    "EDUCATION_LEVEL",
    EDUCATION_LEVEL,
    reduce(() => criteria.education, []),
    false
  );
  addSingleChoicePart(
    "FEMALE_WORK_STUDY",
    FEMALE_WORK_STUDY,
    reduce(() => criteria.femaleWorkOrStudy, "")
  );
  addSingleChoicePart(
    "SMOKING_LEVEL",
    SMOKING_LEVEL,
    reduce(() => criteria.smoking, "")
  );
  addSingleChoicePart(
    "BELONGS_TO_TRIBE",
    BELONG_TO_A_TRIBE,
    reduce(() => criteria.belongsToATribe, "")
  );
  addSingleChoicePart(
    "RELOCATION",
    RELOCATION,
    reduce(() => criteria.relocation, "")
  );
  addMultipleChoicePart(
    "SKIN_COLOR",
    SKIN_COLOR,
    reduce(() => criteria.skinColor, []),
    false
  );
  addMultipleChoicePart(
    "RELIGION",
    RELIGION,
    reduce(() => criteria.religion, []),
    false
  );
  addMultipleChoicePart(
    "VEIL",
    VEIL,
    reduce(() => criteria.veil, []),
    false
  );
  addMultipleChoicePart(
    "INCOME",
    INCOME,
    reduce(() => criteria.income, []),
    false
  );
  addSingleChoicePart(
    "MATCH_APP_LANGUAGE",
    KHATTABAT_MATCHING_RESULTS_MATCH_APP_LANGUAGE,
    reduce(() => criteria.matchAppLanguage, "")
  );

  return isDefinedArray(parts) ? parts.join(" - ") : "-";
};
